import { trackEvent } from 'src/common/3rdParty/tracking';
import { SEARCH_INQUIRY_LATEST_KEY } from 'src/common/constants';
import { SearchInquiryKind } from 'src/common/hooks/useRecentSearches';
import { InstabookAvailability } from 'src/common/types/api/v4/instabook';
import { localStore } from 'src/common/utils/storageWrapper';

type TripCartPrePopulatedParams = {
  originalDates: string[] | undefined;
  originalCapacity: number;
  originalDuration: string | null | undefined;
  mappedDates: string[];
  mappedCapacity: number;
  mappedDuration: string;
};

/**
 * Tracks when trip cart is pre-populated with data from recent searches or inquiries
 * @param params Object containing the cookie data and clamped pre-populated values
 */
export function trackTripCartPrePopulated({
  originalDates,
  mappedDates,
  originalCapacity,
  originalDuration,
  mappedCapacity,
  mappedDuration,
}: TripCartPrePopulatedParams): void {
  if (typeof window === 'undefined') {
    return;
  }
  const searchOrInquiry = localStore.getItem(SEARCH_INQUIRY_LATEST_KEY) as SearchInquiryKind
    || undefined;

  trackEvent('trip-cart_prepopulated', {
    // Note that mapped dates is first to keep similar to historical data
    p1: (mappedDates || []).join(','),
    p2: originalDuration || '',
    p3: mappedDuration || '',
    p4: originalCapacity || '',
    p5: mappedCapacity || '',
    p6: searchOrInquiry,
    // Out of sequence due to being added later
    p7: (originalDates || []).join(','),
  });
}

export type CostType = 'estimated-cost' | 'confirmed-cost';
export type TripCartPriceCalculatedParams = {
  isBookNowVisible: boolean;
  isBookNowEnabled: boolean;
  isMultiDay: boolean;
  costType: CostType;
  capacity: number;
  duration: string;
};

/**
 * Tracks when trip cart price calculation is completed
 * @param params Object containing booking visibility, enabled state, and cost type
 */
export function trackTripCartPriceCalculated({
  isBookNowVisible,
  isBookNowEnabled,
  isMultiDay,
  costType,
  capacity,
  duration,
}: TripCartPriceCalculatedParams): void {
  trackEvent('trip-cart_price-calculated', {
    p1: isBookNowVisible.toString(),
    p2: isBookNowEnabled.toString(),
    p3: isMultiDay.toString(),
    p4: costType,
    p5: capacity,
    p6: duration,
  });
}

type TripCartInquiryClickParams = {
  dates: string[];
  duration: string;
  capacity: string;
  captainOption: string;
  bookNowVisible: string;
  bookNowEnabled: string;
  sameOrMultiDay: string;
};

export function trackTripCartInquiryClick({
  dates,
  duration,
  capacity,
  captainOption,
  bookNowVisible,
  bookNowEnabled,
  sameOrMultiDay,
}: TripCartInquiryClickParams): void {
  trackEvent('trip-cart_send-inquiry-click', {
    p1: (dates || []).join(','),
    p2: duration || '',
    p3: capacity || '',
    p4: captainOption || '',
    p5: bookNowVisible || '',
    p6: bookNowEnabled || '',
    p7: sameOrMultiDay || '',
  });
}

export function trackTripCartBookNowClick(status: 'success' | 'validation-error'): void {
  trackEvent('trip-cart_book-now-click', {
    p1: status,
  });
}

export function trackTripCartBookNowOptionsPanelOpen(): void {
  trackEvent('trip-cart_book-now-options-panel-open');
}

export function trackTripCartBookNowOptionsPanelClose(): void {
  trackEvent('trip-cart_book-now-options-panel-close');
}

export function trackTripCartBookNowOptionClick(option: InstabookAvailability): void {
  trackEvent('trip-cart_book-now-option-click', {
    p1: option.boat_id,
    p2: option.id,
  });
}

export function trackTripCartBookNowProceedToPaymentClick(): void {
  trackEvent('trip-cart_book-now-proceed-to-payment-click');
}

export function trackTripCartBookNowCloseClick(): void {
  trackEvent('trip-cart_book-now-close-click');
}
